"use client";

import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRightLeft,
  BookOpen,
  Blocks,
  ChevronDown,
  DollarSign,
  Hand,
  MessageSquare,
  Search,
  Users,
  type LucideIcon,
} from "lucide-react";
import { DemoCTA, TextLinkCTA, TrishCTA } from "~/components/siteglue/siteglue-cta";
import { AI_BUILT_FOR_HUMANS_LINE } from "~/lib/siteglue/ai-employee-plans";
import { SITEGLUE_COLORS } from "~/lib/siteglue/marketing-chrome";
import { cn } from "~/lib/utils";
type FaqItem = { question: string; answer: string };

type FaqCategory = {
  id: string;
  title: string;
  icon: LucideIcon;
  items: FaqItem[];
};

const FAQ_CATEGORIES: FaqCategory[] = [
  {
    id: "ai-built-for-humans",
    title: "AI Built for Humans",
    icon: Hand,
    items: [
      {
        question: "What makes SiteGlue different?",
        answer:
          "SiteGlue is built around one idea: AI Built for Humans. Always a Human-in-the-Loop™. Your AI employee answers questions, captures leads, and books appointments—while your team stays in control with Team Handoff™ and Team Takeover™. We automate the repetitive work so people can focus on the conversations that need experience, empathy, and judgment.",
      },
      {
        question: "What is Human-in-the-Loop™?",
        answer:
          "Human-in-the-Loop™ means your team can review, monitor, guide, and improve every AI conversation. Automation runs when it should; people step in when it matters. It is the core design principle behind SiteGlue—not an optional add-on.",
      },
      {
        question: "What is Team Handoff™?",
        answer:
          "Team Handoff™ is AI-initiated. Your AI Employee recognizes that a conversation should involve a team member, collects the required context, and routes or transfers the interaction according to your approved rules — across website chat, phone, SMS, email, WhatsApp, and Messenger.",
      },
      {
        question: "What is Team Takeover™?",
        answer:
          "Team Takeover™ is human-initiated or team-accepted. Your staff join or continue an AI-led conversation while preserving available context and pausing or disabling the AI where appropriate — so customers never have to start over.",
      },
    ],
  },
  {
    id: "ai-employees",
    title: "AI Employees",
    icon: Users,
    items: [
      {
        question: "What is an AI employee?",
        answer:
          "An AI employee is a SiteGlue teammate that works alongside your people. It can greet customers, answer questions from your knowledge, capture leads, book appointments, and escalate to humans when needed—across the channels you enable. Think Intern, Associate, Professional, or Enterprise plans—not a black-box chatbot.",
      },
      {
        question: "Can I customize my AI employee?",
        answer:
          "Yes. Give it a name, voice, personality, and brand tone. Train it on your website, documents, and FAQs. Enable the channels and skills you need—scheduling, handoffs, and more—so it sounds like your business and works the way your team does.",
      },
      {
        question: "Can I have multiple AI employees?",
        answer:
          "Yes. Many organizations run multiple agents for different locations, roles, or brands. Each can have its own identity, training, channels, and team handoff settings. Enterprise plans support custom routing and higher volume across your org.",
      },
    ],
  },
  {
    id: "communication-channels",
    title: "Communication Channels",
    icon: MessageSquare,
    items: [
      {
        question: "Which communication channels are supported?",
        answer:
          "SiteGlue works across website chat, phone, SMS, email, WhatsApp, Messenger, and video. Manage conversations from a unified inbox so your team sees every interaction in one place—and can take over when needed.",
      },
      {
        question: "Can my AI answer phone calls?",
        answer:
          "Yes. On Associate, Professional, and Enterprise plans you get a dedicated phone number so your AI employee can answer inbound calls, handle common questions, book appointments when tools are connected, and warm-transfer to your team with context.",
      },
      {
        question: "Can my team take over conversations?",
        answer:
          "Always. That is Team Takeover™ and Team Handoff™ in action. Staff can continue live chats, transfer phone calls, or pick up messaging threads with the full conversation history—so customers are never stranded in a bot loop.",
      },
    ],
  },
  {
    id: "knowledge-training",
    title: "Knowledge & Training",
    icon: BookOpen,
    items: [
      {
        question: "How does my AI learn?",
        answer:
          "You train it on your business: company profile, website content, uploaded documents, FAQs, and other knowledge sources. SiteGlue uses retrieval (RAG) so answers stay grounded in what you provide—not generic internet guesses.",
      },
      {
        question: "Does my AI keep learning?",
        answer:
          "Your AI improves as you update training content, add FAQs from real conversations, and refine instructions. You stay in control of what it knows. Conversation reports also help your team spot gaps to train next.",
      },
    ],
  },
  {
    id: "integrations",
    title: "Integrations",
    icon: Blocks,
    items: [
      {
        question: "What systems integrate with SiteGlue?",
        answer:
          "SiteGlue connects to calendars (Google Calendar, Calendly), EMR/PMS systems via MCP (including Juvonno, NexHealth, and many dental and medical platforms), Zapier, analytics tools, and custom workflows. The goal is to work inside the systems your team already trusts.",
      },
      {
        question: "Can SiteGlue schedule appointments?",
        answer:
          "Yes. When scheduling tools or EMR/PMS integrations are enabled, your AI employee can check availability and book, reschedule, or confirm appointments—then hand off to a human if something needs judgment or a special case.",
      },
      {
        question: "Can SiteGlue update my CRM?",
        answer:
          "SiteGlue captures leads and conversation context, notifies your team, and can connect through integrations and workflows (including Zapier and partner connectors) so contact details and outcomes flow into the systems you use for follow-up. Exact CRM fields depend on your connected tools.",
      },
    ],
  },
  {
    id: "pricing-security",
    title: "Pricing & Security",
    icon: DollarSign,
    items: [
      {
        question: "Can I start for free?",
        answer:
          "Yes. The Intern plan is free—hire your first AI teammate for website chat with included conversation credits and no credit card required. Upgrade to Associate, Professional, or Enterprise when you need phone, more channels, or higher volume.",
      },
      {
        question: "What happens if I exceed conversation credits?",
        answer:
          "Each plan includes monthly conversation credits (for example, Intern 250/mo, Associate 2,000/mo, Professional 10,000/mo; Enterprise is custom). If you exceed included volume, overage applies based on your plan—or you can upgrade for more included capacity. Transparent pricing, no surprise long-term contracts.",
      },
      {
        question: "Is my data secure?",
        answer:
          "Yes. SiteGlue is designed with security and privacy in mind—encryption in transit and at rest, access controls, and audit-friendly conversation history. Healthcare and regulated teams can discuss BAAs and compliance needs with sales. Your knowledge and customer conversations stay under your control.",
      },
      {
        question: "Will AI replace my employees?",
        answer:
          "No. SiteGlue is built so AI helps people do their best work—not replace them. Your AI employee handles repetitive volume; your team owns judgment, relationships, and takeovers. Always a Human-in-the-Loop™.",
      },
      {
        question: "Why did you build SiteGlue?",
        answer:
          "Founder Jim Wanek built SiteGlue because businesses were missing calls, drowning in repetitive questions, and stuck between hiring more staff and shipping chatbots that strand customers. The answer is collaboration: AI when it's helpful, people when it matters. Read more on the About page.",
      },
    ],
  },
];

const CALLOUTS = [
  {
    icon: Hand,
    title: "Human-in-the-Loop™",
    body: "Review, monitor, and guide every AI conversation while staying in complete control.",
  },
  {
    icon: ArrowRightLeft,
    title: "Team Handoff™",
    body: "When automation reaches its limit, transfer to the right person with full context.",
  },
  {
    icon: Users,
    title: "Team Takeover™",
    body: "Continue any conversation across channels without asking the customer to repeat themselves.",
  },
] as const;

function FaqAccordionItem({
  item,
  open,
  onToggle,
  id,
}: {
  item: FaqItem;
  open: boolean;
  onToggle: () => void;
  id: string;
}) {
  return (
    <div
      className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm"
      data-testid={`faq-item-${id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-slate-50/80"
        data-testid={`faq-toggle-${id}`}
      >
        <span className="pr-2 text-sm font-semibold text-slate-900 sm:text-base">
          {item.question}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200",
            open && "rotate-180 text-[#4f81bd]",
          )}
          aria-hidden
        />
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="border-t border-slate-100 px-5 py-4 text-sm leading-relaxed text-slate-600">
              {item.answer}
            </p>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export function SiteGlueFaqPage() {
  const [query, setQuery] = useState("");
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [openCategoryId, setOpenCategoryId] = useState<string | null>("ai-built-for-humans");

  const filteredCategories = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQ_CATEGORIES;
    return FAQ_CATEGORIES.map((cat) => ({
      ...cat,
      items: cat.items.filter(
        (item) =>
          item.question.toLowerCase().includes(q) ||
          item.answer.toLowerCase().includes(q) ||
          cat.title.toLowerCase().includes(q),
      ),
    })).filter((cat) => cat.items.length > 0);
  }, [query]);

  const hasResults = filteredCategories.length > 0;

  return (
    <div className="bg-white text-slate-900">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200/80">
        <div className="pointer-events-none absolute inset-0">
          <div
            className="absolute left-1/2 top-0 h-[480px] w-[780px] -translate-x-1/2 rounded-full opacity-70 blur-[120px]"
            style={{
              background: `radial-gradient(ellipse, ${SITEGLUE_COLORS.blue}18, transparent 60%)`,
            }}
          />
        </div>
        <div className="relative mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 sm:py-24 lg:px-8">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-[#4f81bd]"
          >
            FAQ
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.04 }}
            className="text-4xl font-bold tracking-tight sm:text-5xl"
          >
            {AI_BUILT_FOR_HUMANS_LINE}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="mt-4 text-xl font-medium text-slate-600"
          >
            Always a Human-in-the-Loop™.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-500"
          >
            Answers about AI employees, channels, training, integrations, pricing, and how your
            team stays in control.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.16 }}
            className="relative mx-auto mt-10 max-w-xl"
          >
            <label htmlFor="faq-search" className="sr-only">
              Search FAQs
            </label>
            <Search
              className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
              aria-hidden
            />
            <input
              id="faq-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions…"
              className="h-12 w-full rounded-full border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-900 shadow-sm outline-none ring-[#4f81bd]/30 transition placeholder:text-slate-400 focus:border-[#4f81bd]/40 focus:ring-2"
              data-testid="input-faq-search"
            />
          </motion.div>
        </div>
      </section>

      {/* HITL callouts */}
      <section className="border-b border-slate-200/80 bg-[#fafbfc] py-12 sm:py-16">
        <div className="mx-auto grid max-w-6xl gap-4 px-4 sm:grid-cols-3 sm:px-6 lg:px-8">
          {CALLOUTS.map((card, index) => (
            <motion.article
              key={card.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.06 }}
              className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm"
            >
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[#4f81bd]/10 text-[#4f81bd]">
                <card.icon className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <h2 className="text-base font-semibold tracking-tight text-slate-900">{card.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{card.body}</p>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section id="faq" data-testid="section-faq" className="py-16 sm:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          {!hasResults ? (
            <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center text-sm text-slate-500">
              No questions match “{query}”. Try another search or{" "}
              <button
                type="button"
                className="font-medium text-[#4f81bd] underline-offset-2 hover:underline"
                onClick={() => setQuery("")}
              >
                clear the search
              </button>
              .
            </p>
          ) : (
            <div className="space-y-6">
              {filteredCategories.map((category) => {
                const Icon = category.icon;
                const categoryOpen =
                  Boolean(query.trim()) || openCategoryId === category.id;
                return (
                  <div key={category.id} className="scroll-mt-28" id={category.id}>
                    <button
                      type="button"
                      onClick={() =>
                        setOpenCategoryId((current) =>
                          current === category.id ? null : category.id,
                        )
                      }
                      className="mb-3 flex w-full items-center gap-3 rounded-xl px-1 py-2 text-left transition hover:bg-slate-50"
                      aria-expanded={categoryOpen}
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#4f81bd]/10 text-[#4f81bd]">
                        <Icon className="h-5 w-5" strokeWidth={1.75} />
                      </span>
                      <span className="flex-1 text-lg font-semibold tracking-tight text-slate-900 sm:text-xl">
                        {category.title}
                      </span>
                      <ChevronDown
                        className={cn(
                          "h-4 w-4 text-slate-400 transition-transform",
                          categoryOpen && "rotate-180",
                        )}
                      />
                    </button>

                    <AnimatePresence initial={false}>
                      {categoryOpen ? (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <div className="space-y-2 pb-2">
                            {category.items.map((item, index) => {
                              const key = `${category.id}-${index}`;
                              return (
                                <FaqAccordionItem
                                  key={key}
                                  id={key}
                                  item={item}
                                  open={openKey === key}
                                  onToggle={() =>
                                    setOpenKey((current) => (current === key ? null : key))
                                  }
                                />
                              );
                            })}
                          </div>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Still have questions */}
      <section className="border-t border-slate-200/80 bg-[#fafbfc] py-20 sm:py-24">
        <div className="mx-auto max-w-2xl px-4 text-center sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Still have questions?
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              Book a demo or talk with our team about how an AI employee could work for
              your business.
            </p>
            <p className="mt-4 text-base font-medium text-slate-800">
              SiteGlue is the AI platform that keeps humans in control.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <DemoCTA location="final_cta" testId="button-faq-book-demo" />
            </div>
            <div className="mt-6 flex flex-col items-center justify-center gap-3">
              <TextLinkCTA
                action="signup"
                location="final_cta"
                label="Or get SiteGlue free"
                testId="button-faq-get-siteglue-free"
              />
              <p className="text-sm text-slate-600">
                Need personalized recommendations?{" "}
                <TrishCTA
                  location="faq_footer"
                  variant="text"
                  size="sm"
                  className="inline-flex min-h-11 items-center text-sm font-semibold"
                  testId="button-faq-start-with-trish"
                />
              </p>
            </div>
            <p className="mt-8 text-sm text-slate-500">
              {AI_BUILT_FOR_HUMANS_LINE} · Always a Human-in-the-Loop™
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
